import React, { useCallback, useEffect, useState } from 'react';
import { getNurseActivity } from '../../api/dashboard';
import StatusMessage from '../../components/StatusMessage';
import { formatDateTime, formatShortDate } from '../../utils/format';
import { adminErrorMessage } from './adminErrors';
import { pct } from './insightsUtils';
import { INACTIVE_DAYS, daysAgo, handoffs, isInactive } from './nurseActivityUtils';

function Fact({ value, label, sub }) {
  return (
    <div className="stat-card stat-card-static">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

const SHIFTS = [{ key: 'day', label: 'Day shift' }, { key: 'night', label: 'Night shift' }];

// The admin's view of how a nurse is using the app: adoption and activity over
// the last 30 days, and their handoff coverage by shift. Aggregates only; an
// admin never sees a report sheet.
const NurseActivity = ({ nurseId, firstName }) => {
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setActivity(null);
    try {
      setActivity(await getNurseActivity(nurseId));
    } catch (err) {
      setError(adminErrorMessage(err, "We couldn't load this nurse's activity."));
    }
  }, [nurseId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>;
  if (!activity) return <StatusMessage kind="loading">Loading activity…</StatusMessage>;

  const who = firstName || 'This nurse';
  const joined = formatShortDate(activity.accountCreatedAt);

  return (
    <>
      <section className="detail-section" aria-label="Adoption and activity">
        <h2 className="section-title">Adoption and activity · last {activity.days} days</h2>
        {isInactive(activity) && (
          <p className="activity-flag" role="note">
            {activity.lastHandoffAt
              ? `${who} hasn't recorded a handoff in ${INACTIVE_DAYS}+ days.`
              : `${who} hasn't recorded a handoff yet.`}
          </p>
        )}
        <div className="stats-row key-facts">
          <Fact value={activity.handoffCount} label="Handoffs" sub={`Last ${activity.days} days`} />
          {activity.activeShiftCount != null && (
            <Fact value={activity.activeShiftCount} label="Shifts with a handoff" sub={`Last ${activity.days} days`} />
          )}
          <Fact
            value={daysAgo(activity.lastHandoffAt)}
            label="Last handoff"
            sub={activity.lastHandoffAt ? formatDateTime(activity.lastHandoffAt) : 'None recorded'}
          />
          <Fact value={activity.currentPatientCount} label="Current patients" sub={joined ? `Account created ${joined}` : undefined} />
        </div>
      </section>

      {activity.shifts && (
        <section className="detail-section" aria-label="Shift split">
          <h2 className="section-title">Shift split</h2>
          <div className="stats-row key-facts">
            {SHIFTS.map(({ key, label }) => {
              const shift = activity.shifts[key] || { handoffCount: 0, averageMentionedPct: null };
              return (
                <Fact
                  key={key}
                  value={shift.handoffCount ? pct(shift.averageMentionedPct) : '—'}
                  label={`${label} coverage`}
                  sub={shift.handoffCount ? handoffs(shift.handoffCount) : 'No handoffs'}
                />
              );
            })}
          </div>
          <p className="admin-hint">
            Coverage is the share of each handoff&apos;s checklist items that were mentioned, averaged over the
            nurse&apos;s handoffs in that shift. Handoffs recorded 1 PM–1 AM count toward the day shift and 1 AM–1 PM
            toward the night shift, as on Insights.
          </p>
        </section>
      )}
    </>
  );
};

export default NurseActivity;
